import { useState, useRef } from 'react';
import { Upload, X, Loader2, Images } from 'lucide-react';
import { getImageUrl } from '../lib/utils-img';
import API from '../lib/api';
import { toast } from 'sonner';

export default function MultiImageUpload({ images = [], onChange, label = 'Gallery Images' }) {
  const [uploading, setUploading] = useState(false);
  const [uploadCount, setUploadCount] = useState({ done: 0, total: 0 });
  const inputRef = useRef(null);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const validFiles = files.filter(f => {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name} is too large (max 10MB)`);
        return false;
      }
      return true;
    });

    if (!validFiles.length) return;

    setUploading(true);
    setUploadCount({ done: 0, total: validFiles.length });
    const newUrls = [];

    for (let i = 0; i < validFiles.length; i++) {
      try {
        const formData = new FormData();
        formData.append('file', validFiles[i]);
        const { data } = await API.post('/admin/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        newUrls.push(data.url);
        setUploadCount(c => ({ ...c, done: c.done + 1 }));
      } catch {
        toast.error(`Failed to upload ${validFiles[i].name}`);
      }
    }

    if (newUrls.length > 0) {
      onChange([...images, ...newUrls]);
      toast.success(`${newUrls.length} image${newUrls.length > 1 ? 's' : ''} uploaded`);
    }

    setUploading(false);
    setUploadCount({ done: 0, total: 0 });
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeImage = (index) => {
    onChange(images.filter((_, i) => i !== index));
  };

  return (
    <div>
      {label && <label className="text-[10px] uppercase tracking-widest text-white/35 mb-1.5 block font-medium">{label}</label>}

      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3" data-testid="gallery-images-grid">
          {images.map((img, i) => (
            <div key={i} className="relative group rounded-lg overflow-hidden border border-white/[0.08] aspect-square">
              <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeImage(i)}
                className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`remove-gallery-image-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="w-full h-32 border-2 border-dashed border-white/[0.10] rounded-xl flex flex-col items-center justify-center gap-2 text-white/25 hover:border-gold hover:text-gold transition-colors bg-[#1a1a1a]"
        data-testid="multi-image-upload-btn"
      >
        {uploading ? (
          <>
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="text-xs">Uploading {uploadCount.done}/{uploadCount.total}...</span>
          </>
        ) : (
          <>
            <Images className="w-6 h-6" />
            <span className="text-xs">Click to upload multiple images</span>
          </>
        )}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFiles}
        className="hidden"
        data-testid="multi-image-input"
      />
    </div>
  );
}
